/**
 * Workspace selection hook (v2.3.0).
 *
 * Provides current workspace, available workspaces (from switch-context), and
 * a manual switch function that calls Central directly.
 *
 * IMPORTANT — 2-step workspace switch:
 *   Step 1: POST {CENTRAL_URL}/api/auth/select-workspace  { workspaceId }
 *           → returns { accessToken, expiresIn } (new token scoped to new workspace)
 *   Step 2: GET  {CENTRAL_URL}/api/suite/switch-context    (Bearer <new token>)
 *           → returns { workspace, otherWorkspaces, entitlementsEffective }
 *
 * Why two steps?
 *   GET switch-context returns the context for whichever workspace the token is
 *   scoped to. It does NOT accept a workspaceId param. To switch, you must first
 *   get a new token via /api/auth/select-workspace, then call switch-context.
 *
 * All switching is manual (user-initiated) — no auto-switch.
 *
 * CRITICAL RULES (prevent spurious login redirects during switch):
 *   - Background 401s during a switch never end the session (store/api.ts
 *     ignores them while isSwitchingWorkspace is set).
 *   - The switch's OWN answers go through the shared classifier
 *     (auth/centralFailureContract). Only a terminal session code
 *     (SESSION_EXPIRED, SESSION_REVOKED, …) clears auth — terminal auth outranks
 *     every generic error. A code-less 401 is never a logout.
 *   - Step 1 refusals leave the user in their current workspace with a message.
 *   - Step 2 failures are boot failures (BootGate.applyBootFailure).
 *   - isSwitchingWorkspace stays true until well after boot completes.
 */

import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import {
  selectCurrentCentralWorkspace,
  selectOtherWorkspaces,
  selectIsCentralAuthenticated,
  selectCentralAccessToken,
  selectIsSwitchingWorkspace,
  selectTokenWorkspaceId,
  selectBootCompleted,
  selectCentralWorkspace,
  setSwitchingWorkspace,
  setBootReady,
  setBootUnavailable,
  updateCentralAccessToken,
  centralLogout,
} from '../store/slices/centralAuthSlice';
import { setBootUnauthenticated } from '../store/slices/bootContextSlice';
import { api } from '../store/api';
import { centralAuthApi } from '../store/api/centralAuthApi';
import { CENTRAL_URL, APP_ID } from '../config/centralAuth';
import { classifyCentralFailure } from '../auth/centralFailureContract';
import { applyBootFailure, TRANSPORT_FAILURE } from '../components/routing/BootGate';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dispatch = (action: any) => unknown;

/**
 * True only when the switch has nothing to do: the app is booted (ready) and
 * the TOKEN is already scoped to the target. Never decided from
 * `centralAuth.selectedWorkspace`, which a token-only switch leaves stale, and
 * never in a refused/closed state, where the token's own workspace is exactly
 * the one that failed.
 */
export function switchIsNoop(input: {
  targetWorkspaceId: string;
  tokenWorkspaceId: string | null;
  bootReady: boolean;
}): boolean {
  return input.bootReady && input.tokenWorkspaceId !== null && input.tokenWorkspaceId === input.targetWorkspaceId;
}

/** User-facing words for a failed switch, from the shared classifier's verdict. */
export function switchFailureMessage(status: number | string, body: unknown): string {
  const { action } = classifyCentralFailure({ status, body });
  switch (action) {
    case 'CENTRAL_LOGIN':
    case 'RETRY_AFTER_REFRESH':
      return 'Your LevelUp session has ended. Sign in again to continue.';
    case 'WORKSPACE_CLOSED':
      return 'That workspace has been closed.';
    case 'WORKSPACE_PICKER':
      return 'You no longer have access to that workspace.';
    case 'APP_ACCESS_DENIED':
      return "BeHeard isn't assigned to you in that workspace.";
    case 'OUTAGE':
      return 'LevelUp is unavailable right now. Try again shortly.';
    default:
      return status === 403
        ? "You don't have access to that workspace."
        : `Couldn't switch workspace (${status}). Try again.`;
  }
}

/**
 * Step 1 (select-workspace) failed. The current token and context are intact,
 * so only a TERMINAL session verdict changes app state (Central login, which
 * outranks everything); every other verdict is reported to the caller and the
 * user stays in their current workspace. Returns the message to show.
 */
export function applySelectWorkspaceFailure(status: number, body: unknown, dispatch: Dispatch): string {
  const decision = classifyCentralFailure({ status, body });
  if (decision.action === 'CENTRAL_LOGIN') {
    // Same transition BootGate makes for a dead session: clear auth; AuthGuard
    // owns the one redirect to Central login.
    dispatch(centralLogout());
    dispatch(setBootUnauthenticated());
  }
  return switchFailureMessage(status, body);
}

export function useWorkspace() {
  const dispatch = useDispatch<AppDispatch>();

  const currentWorkspace = useSelector(selectCurrentCentralWorkspace);
  const otherWorkspaces = useSelector(selectOtherWorkspaces);
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const accessToken = useSelector(selectCentralAccessToken);
  const isSwitching = useSelector(selectIsSwitchingWorkspace);
  const tokenWorkspaceId = useSelector(selectTokenWorkspaceId);
  const bootReady = useSelector(selectBootCompleted);

  // Combine current workspace + otherWorkspaces for the selector dropdown
  const allWorkspaces = currentWorkspace
    ? [currentWorkspace, ...otherWorkspaces.filter((ws) => ws.id !== currentWorkspace.id)]
    : [...otherWorkspaces];

  /**
   * Switch to a different workspace (manual, user-initiated only).
   *
   * Flow:
   * 1. Show blocking "Switching workspace…" overlay
   * 2. POST /api/auth/select-workspace → new token for target workspace
   * 3. GET  /api/suite/switch-context  → full context with new token
   * 4. On success: store context + token, reset caches, stay on current route
   * 5. Step 1 refused: classified; terminal session → Central login, anything
   *    else → an error message, user stays in the current workspace
   * 6. Step 2 refused: BootGate.applyBootFailure (closed / denied overlay with
   *    its reason, Central login for a terminal code, outage for 5xx)
   *
   * Never navigates itself: AuthGuard owns the one redirect to Central login.
   */
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      if (!isAuthenticated || !accessToken) {
        return { success: false as const, error: 'Not authenticated' };
      }

      // Already there? Decided by the TOKEN's workspace and only in a ready app
      // (QA-B P2). `selectedWorkspace` is not updated by a token-only switch, so
      // comparing against it made a real switch a silent "success" no-op — and
      // from the refusal overlay the refused workspace is never "already there".
      if (switchIsNoop({ targetWorkspaceId: workspaceId, tokenWorkspaceId, bootReady })) {
        return { success: true as const };
      }

      // 1. Show blocking overlay (hides children → deactivates RTK Query subscriptions).
      //    This flag stays true until we explicitly clear it with a grace period.
      dispatch(setSwitchingWorkspace(true));
      let tokenSwapped = false;

      try {
        // ── Step 1: Get new token scoped to target workspace ──
        const selectRes = await fetch(`${CENTRAL_URL}/api/auth/select-workspace`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ workspaceId }),
        });

        // Step 1 failed: the CURRENT token and context are untouched, so the
        // user stays where they are. The answer goes through the ONE shared
        // classifier (BH-06): a terminal session code still reaches Central
        // login (terminal auth outranks every generic error), and everything
        // else is a message the caller shows — never the outage overlay for a
        // refusal Central plainly answered.
        if (!selectRes.ok) {
          const errData = await selectRes.json().catch(() => undefined);
          console.error('[useWorkspace] select-workspace failed:', selectRes.status, errData);
          dispatch(setSwitchingWorkspace(false));
          return { success: false as const, error: applySelectWorkspaceFailure(selectRes.status, errData, dispatch) };
        }

        const selectData = await selectRes.json();
        const newToken: string = selectData.accessToken;
        const newExpiresIn: number = selectData.expiresIn ?? 3600;

        if (!newToken) {
          console.error('[useWorkspace] select-workspace returned no accessToken');
          dispatch(setBootUnavailable('No token returned'));
          dispatch(setSwitchingWorkspace(false));
          return { success: false as const, error: 'No token returned' };
        }

        // Store new token immediately (single source of truth)
        dispatch(updateCentralAccessToken({ accessToken: newToken, expiresIn: newExpiresIn }));
        tokenSwapped = true;

        // ── Step 2: Get full context with the new token ──
        const contextRes = await fetch(`${CENTRAL_URL}/api/suite/switch-context`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${newToken}`,
            'X-App-Id': APP_ID,
          },
        });

        if (contextRes.ok) {
          const ctxData = await contextRes.json();
          // If switch-context returns yet another token, use it
          if (ctxData.accessToken) {
            dispatch(updateCentralAccessToken({
              accessToken: ctxData.accessToken,
              expiresIn: ctxData.expiresIn,
            }));
          }

          // Keep centralAuth.selectedWorkspace in step with the token. Only the
          // token used to change, so selectedWorkspace kept naming the workspace
          // the user signed in to (QA-B P2). Workspace only — no token fields.
          if (ctxData.workspace?.id) {
            dispatch(selectCentralWorkspace({ workspace: ctxData.workspace } as never));
          }

          // Clear workspace-scoped caches NOW (children are hidden behind
          // the switching overlay, so no active subscriptions will re-fire).
          dispatch(api.util.resetApiState());
          dispatch(centralAuthApi.util.resetApiState());

          // Store new context (does NOT clear isSwitchingWorkspace).
          dispatch(
            setBootReady({
              currentWorkspace: ctxData.workspace,
              otherWorkspaces: ctxData.otherWorkspaces ?? [],
              apps: ctxData.apps ?? [],
              user: ctxData.user ?? null,
              meta: ctxData.meta,
            })
          );

          // Stay on current route — no navigate('/dashboard'), no reload.
          // Clear the switching guard with a grace period so post-switch
          // RTK Query re-fetches don't race with baseQueryWithReauth.
          setTimeout(() => {
            dispatch(setSwitchingWorkspace(false));
          }, 1500);

          return { success: true as const };
        }

        // Step 2 failed: the NEW token is already stored, so the app is now in
        // the target workspace and its context could not be loaded. That is
        // exactly a boot failure, so it gets exactly the boot decision —
        // BootGate's applyBootFailure, on the shared classifier — instead of the
        // old hand-rolled branches (which turned a terminal 401 into "please
        // sign out" text and every 403 into the outage overlay).
        const data = await contextRes.json().catch(() => undefined);
        dispatch(setSwitchingWorkspace(false));
        applyBootFailure(contextRes.status, data, dispatch);
        return { success: false as const, error: switchFailureMessage(contextRes.status, data) };
      } catch (error) {
        console.error('[useWorkspace] Switch error:', error);
        dispatch(setSwitchingWorkspace(false));
        // Once the new token is stored the old context no longer matches it, so
        // a transport failure there fails closed to the outage overlay (as it
        // always did). Before that, nothing changed: the user stays put.
        if (tokenSwapped) applyBootFailure(TRANSPORT_FAILURE, undefined, dispatch);
        return { success: false as const, error: 'Unable to reach LevelUp. Check your connection and try again.' };
      }
    },
    [isAuthenticated, accessToken, tokenWorkspaceId, bootReady, dispatch]
  );

  return {
    currentWorkspace,
    workspaces: allWorkspaces,
    otherWorkspaces,
    switchWorkspace,
    isLoading: isSwitching,
    hasMultipleWorkspaces: allWorkspaces.length > 1,
  };
}
